import React, { useState } from "react";
import { Library } from "lucide-react";
import { toast } from "sonner";

import { BrandButton } from "@/shared/components/ui-kit";
import { inputCls } from "@/shared/components/task-atoms";
import { confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  useCreateKnowledgeBase, useDeleteKnowledgeBase, usePatchKnowledgeBase,
} from "@/shared/api/knowledge";

import type { KnowledgeBase } from "@/shared/types";

const errorText = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong");

/* One dialog for both writes: with `base` it renames and edits the description,
   without it creates. It stays open on failure so nothing typed is lost. */
export function KbFormDialog({ base, onClose }: { base?: KnowledgeBase; onClose: () => void }) {
  const [name, setName] = useState(base?.name ?? "");
  const [description, setDescription] = useState(base?.description ?? "");
  const create = useCreateKnowledgeBase();
  const patch = usePatchKnowledgeBase();
  const pending = create.isPending || patch.isPending;
  const trimmed = name.trim();

  const submit = () => {
    if (!trimmed || pending) return;
    const body = { name: trimmed, description: description.trim() };
    const opts = { onSuccess: onClose, onError: (e: unknown) => toast.error(errorText(e)) };
    if (base) patch.mutate({ kbId: base.id, body }, opts);
    else create.mutate(body, opts);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[440px] p-0 gap-0 rounded-[16px] bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] flex items-center gap-2">
            <Library className="w-4 h-4 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            {base ? "Edit knowledge base" : "New knowledge base"}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[var(--text-tertiary)] mt-1">
            A shared library of files. Link it to agents to let them search and cite it.
          </DialogDescription>
        </div>
        <div className="px-5 py-4 space-y-3">
          <input
            autoFocus
            aria-label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="e.g. Product docs"
            className={inputCls}
          />
          <textarea
            aria-label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is in it (optional)"
            rows={3}
            className={`${inputCls} h-auto py-2 resize-none`}
          />
        </div>
        <div className="px-5 py-3.5 border-t hairline flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="press h-8 px-3.5 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
          >
            Cancel
          </button>
          <BrandButton onClick={submit} disabled={!trimmed || pending} className="disabled:opacity-40 disabled:pointer-events-none">
            {base ? "Save" : "Create"}
          </BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteKbDialog({ base, onClose }: { base: KnowledgeBase; onClose: () => void }) {
  const remove = useDeleteKnowledgeBase();
  const files = `${base.file_count} ${base.file_count === 1 ? "file" : "files"}`;

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Delete {base.name}?</AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-[1.6]">
            Deletes {files}. Agents linked to it lose it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full text-[13px]">Cancel</AlertDialogCancel>
          <button
            className={confirmDangerCls}
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(base.id, {
                onSuccess: onClose,
                onError: (e) => toast.error(errorText(e)),
              })
            }
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
